import React, { ReactNode, memo, useCallback, useState, useMemo } from 'react';
import cx from 'clsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { useTranslation } from 'react-i18next';

import { Loading } from '@/components';

import style from './index.module.scss';

export type DateBarChartRecord = {
  value: number;
  timestamp?: number;
  _date: string;
  _value: string;
};

type DateBarChartProps = {
  movePlaceholder?: ReactNode;
  emptyPlaceholder?: ReactNode;
  data?: DateBarChartRecord[];
  isLoading?: boolean;
  className?: string;
  moveTitleClassName?: string;
  moveDescClassName?: string;
  chartWidth?: number;
  chartHeight?: number;
  barSize?: number;
  chartMargin?: number;
  domainMargin?: number;
};

export const DateBarChart = memo(
  ({
    movePlaceholder = '',
    emptyPlaceholder,
    data,
    isLoading,
    className,
    moveTitleClassName,
    moveDescClassName,
    chartWidth = 256,
    chartHeight = 130,
    barSize = 20,
    chartMargin = 10,
    domainMargin = 0,
  }: DateBarChartProps) => {
    const [focusBar, setFocusBar] = useState<number | null>(null);
    const { t } = useTranslation();

    const handleMouseEnter = useCallback(
      ({
        isTooltipActive,
        activeTooltipIndex,
      }: {
        isTooltipActive?: boolean;
        activeTooltipIndex?: number;
      }) => {
        if (isTooltipActive) {
          setFocusBar(activeTooltipIndex!);
        } else {
          setFocusBar(null);
        }
      },
      [],
    );

    const handleMouseLeave = useCallback(() => {
      setFocusBar(null);
    }, []);

    const currentBar = useMemo(
      () => (focusBar !== null ? data?.[focusBar] : undefined),
      [data, focusBar],
    );

    const isNodata = useMemo(() => {
      return !data?.some(x => !!x.value);
    }, [data]);

    const heightStyle = useMemo(() => ({ height: chartHeight }), [chartHeight]);

    return (
      <div className={cx(style.container, className)}>
        <div className={style.moveInfo}>
          {currentBar ? (
            <>
              <div className={cx(style.moveTitle, moveTitleClassName)}>{currentBar._date}</div>
              <div className={cx(style.moveDesc, moveDescClassName)}>{currentBar._value}</div>
            </>
          ) : (
            movePlaceholder
          )}
        </div>
        {isLoading ? (
          <Loading className={style.loading} style={heightStyle} />
        ) : !isNodata ? (
          <div className={style.chart}>
            <BarChart
              width={chartWidth}
              height={chartHeight}
              margin={{ bottom: 0, left: chartMargin, right: chartMargin, top: 10 }}
              data={data}
              onMouseMove={handleMouseEnter}
              onMouseLeave={handleMouseLeave}>
              <Bar dataKey="value" barSize={barSize}>
                {data!.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={focusBar === index ? ' var(--color-blue)' : 'var(--dialog-color)'}
                  />
                ))}
              </Bar>
              <Tooltip cursor={false} content={() => null} />
              <YAxis
                hide
                domain={[
                  (dataMin: number) => dataMin * (1 - domainMargin),
                  (dataMax: number) => dataMax * (1 + domainMargin),
                ]}
              />
              {data?.[0]?.timestamp ? (
                <XAxis dataKey="timestamp" type="number" hide domain={['dataMin', 'dataMax']} />
              ) : (
                <XAxis dataKey="_date" hide />
              )}
            </BarChart>
          </div>
        ) : (
          <div className={style.empty} style={heightStyle}>
            {emptyPlaceholder || t('DateBarChart.nohistory')}
          </div>
        )}
        <div className={style.xaxis}>
          <span>{data?.[0]?._date || ''}</span>
          <span>{data?.[data.length - 1]?._date || ''}</span>
        </div>
      </div>
    );
  },
);
